import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Gamepad2, 
  FileText, 
  Settings, 
  Building, 
  User, 
  UserPlus, 
  UserCheck, 
  Clock,
  ShoppingCart,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Bug
} from 'lucide-react';
import { useSelector } from 'react-redux';
import styles from './css/Sidebar.module.css';
import { usePrivilege } from '../hooks/usePrivilege';

/**
 * MenuItem Component - Individual menu item with icon and label
 * @param {Object} props - Component props
 * @param {ReactNode} props.icon - Icon component
 * @param {string} props.label - Menu item label
 * @param {string} props.to - Route path
 * @param {boolean} props.isActive - Whether the item is active
 * @param {boolean} props.collapsed - Whether sidebar is collapsed
 */
const MenuItem = ({ icon, label, to, isActive, collapsed }) => (
  <Link 
    to={to} 
    className={`${styles.menuItem} ${isActive ? styles.menuItemActive : ''} ${collapsed ? styles.menuItemCollapsed : ''}`}
    title={collapsed ? label : ''}
  >
    <span className={styles.menuIcon}>{icon}</span>
    {!collapsed && <span className={styles.menuLabel}>{label}</span>}
    {isActive && <span className={styles.activeIndicator}></span>}
  </Link>
);

/**
 * SidebarSection Component - Accordion section with expandable children
 * @param {Object} props - Component props
 * @param {ReactNode} props.icon - Section icon
 * @param {string} props.label - Section label
 * @param {ReactNode} props.children - Child menu items
 * @param {boolean} props.collapsed - Whether sidebar is collapsed
 */
const SidebarSection = ({ icon, label, children, collapsed }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    if (!collapsed) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className={styles.sidebarSection}>
      <button
        className={`${styles.sectionHeader} ${isExpanded ? styles.sectionHeaderExpanded : ''} ${collapsed ? styles.sectionHeaderCollapsed : ''}`}
        onClick={toggleExpand}
        title={collapsed ? label : ''}
        disabled={collapsed}
      >
        <span className={styles.sectionIcon}>{icon}</span>
        {!collapsed && (
          <>
            <span className={styles.sectionLabel}>{label}</span>
            <span className={styles.sectionChevron}>
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          </>
        )}
      </button>
      {!collapsed && (
        <div className={`${styles.sectionContent} ${isExpanded ? styles.sectionContentExpanded : ''}`}>
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Sidebar Component - Main sidebar navigation component
 * Enterprise-grade sidebar with responsive design and role-based access control
 */
const Sidebar = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const slug = useMemo(() => {
    const pathParts = location.pathname.split('/');
    return pathParts[1] || 'root';
  }, [location.pathname]);
  
  const { user } = useSelector(state => state.auth);
  const role = user?.role;
  const { can } = usePrivilege();

  // Privilege-based access (super_admin always true via hook)
  const canAccessGameManagement = can('game.manage');
  const canAccessDrawManagement = can('draw.manage');
  const canAccessCurrentDraws = can('draw.current');
  const canAccessDrawsByGame = can('draw.byGame');
  const canAccessResultControl = can('result.control');
  const canAccessEngineDebug = can('result.engineDebug');
  const showGameSection =
    canAccessGameManagement ||
    canAccessDrawManagement ||
    canAccessCurrentDraws ||
    canAccessDrawsByGame ||
    canAccessResultControl ||
    canAccessEngineDebug;

  const canViewAdminMenu = can('admin.view');
  const canViewSuperStokezMenu = can('superStokez.view');
  const canViewStokezMenu = can('stokez.view');
  const canViewAgentMenu = can('agent.view');
  const canViewPlayerMenu = can('player.view');

  const canViewSuperStockezWiseReport = can('report.superStokez');
  const canViewStokezWiseReport = can('report.stokez');
  const canViewAgentWiseReport = can('report.agent');
  const canViewPlayerWiseReport = can('report.player');
  const canViewPrivilegeMenu = can('privilege.manage');
  const canViewPrivilegeCatalog = role === 'super_admin';
  const canViewSettings = can('settings.manage');
  const canViewTransactionReport = can('report.transaction');
  const canViewActivityLog = can('report.activityLog');
  const canViewSummaryReport = can('report.summary');
  const canViewAllTicket = can('report.allTicket');
  const canViewGameWiseReport = can('report.gameWise');
  const canViewSearchBarcode = can('report.searchBarcode');
  const canViewCompany = can('company.view');
  const showSalesReportSection =
    canViewSummaryReport ||
    canViewAllTicket ||
    canViewSuperStockezWiseReport ||
    canViewStokezWiseReport ||
    canViewAgentWiseReport ||
    canViewPlayerWiseReport ||
    canViewGameWiseReport ||
    canViewSearchBarcode;

  /**
   * Gets the full path with slug prefix
   * @param {string} path - Route path
   * @returns {string} Full path with slug
   */
  const getPath = (path) => {
    return `/${slug}${path}`;
  };

  /**
   * Toggles mobile sidebar
   */
  const toggleMobile = () => {
    setMobileOpen(!mobileOpen);
  };

  /**
   * Closes mobile sidebar
   */
  const closeMobile = () => {
    setMobileOpen(false);
  };

  /**
   * Prevents body scroll when mobile sidebar is open
   */
  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [mobileOpen]);

  /**
   * Updates CSS variable for sidebar width to adjust main content
   */
  useEffect(() => {
    const root = document.documentElement;
    const handleResize = () => {
      // On mobile, always set to 0 (sidebar is overlay)
      if (window.innerWidth < 768) {
        root.style.setProperty('--sidebar-width', '0px');
      } else {
        // Update when collapsed state changes on desktop
        if (collapsed) {
          root.style.setProperty('--sidebar-width', '70px');
        } else {
          root.style.setProperty('--sidebar-width', '260px');
        }
      }
    };

    // Initialize on mount
    if (!root.style.getPropertyValue('--sidebar-width')) {
      root.style.setProperty('--sidebar-width', '260px');
    }
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [collapsed]);

  /**
   * Closes mobile sidebar when route changes
   */
  useEffect(() => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* Mobile Menu Button */}
      <button 
        className={styles.mobileToggle}
        onClick={toggleMobile}
        aria-label="Toggle menu"
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className={styles.mobileOverlay}
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''} ${mobileOpen ? styles.sidebarMobileOpen : ''}`}
      >
        {/* Sidebar Header */}
        <div className={styles.sidebarHeader}>
          {!collapsed && (
            <div className={styles.logoSection}>
              <h2 className={styles.logoText}>Control Panel</h2>
              <span className={styles.logoSubtext}>Admin Panel</span>
            </div>
          )}
          <button
            className={styles.collapseButton}
            onClick={() => {
              setCollapsed(!collapsed);
              if (mobileOpen) closeMobile();
            }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>

        {/* Navigation Content */}
        <nav className={styles.navContent}>
          <div className={styles.navSection}>
            <MenuItem
              collapsed={collapsed}
              icon={<Home size={20} />}
              label="Dashboard"
              to={getPath('/dashboard')}
              isActive={location.pathname === getPath('/dashboard')}
            />

            {/* Company / Brand Section */}
            {canViewCompany && (
              <SidebarSection collapsed={collapsed} icon={<Building size={20} />} label="Company / Brand">
                <MenuItem
                  collapsed={collapsed}
                  icon={<Settings size={18} />}
                  label="Manage Company"
                  to="/root/manage-company"
                  isActive={location.pathname === '/root/manage-company'}
                />
              </SidebarSection>
            )}

            {/* Game Section */}
            {showGameSection && (
              <SidebarSection collapsed={collapsed} icon={<Gamepad2 size={20} />} label="Game">
                {canAccessGameManagement && (
                  <MenuItem
                    collapsed={collapsed}
                    icon={<Settings size={18} />}
                    label="Game Management"
                    to={getPath('/games')}
                    isActive={location.pathname === getPath('/games')}
                  />
                )}
                {canAccessDrawManagement && (
                  <MenuItem
                    collapsed={collapsed}
                    icon={<Clock size={18} />}
                    label="Draw Management"
                    to={getPath('/draws')}
                    isActive={location.pathname === getPath('/draws')}
                  />
                )}
                {canAccessCurrentDraws && (
                  <MenuItem
                    collapsed={collapsed}
                    icon={<Clock size={18} />}
                    label="Current Draws"
                    to={getPath('/current-draws')}
                    isActive={location.pathname === getPath('/current-draws')}
                  />
                )}
                {canAccessDrawsByGame && (
                  <MenuItem
                    collapsed={collapsed}
                    icon={<Clock size={18} />}
                    label="Draws By Game"
                    to={getPath('/draws-by-game')}
                    isActive={location.pathname === getPath('/draws-by-game')}
                  />
                )}
                {canAccessResultControl && (
                  <MenuItem
                    collapsed={collapsed}
                    icon={<Clock size={18} />}
                    label="Result Control - DS"
                    to={getPath('/ds')}
                    isActive={location.pathname === getPath('/ds')}
                  />
                )}
                {canAccessEngineDebug && (
                  <MenuItem
                    collapsed={collapsed}
                    icon={<Bug size={18} />}
                    label="Engine Debug"
                    to={getPath('/engine-debug')}
                    isActive={location.pathname === getPath('/engine-debug')}
                  />
                )}
              </SidebarSection>
            )}

            {/* User Management Section */}
            <SidebarSection collapsed={collapsed} icon={<Users size={20} />} label="User Management">
              {canViewAdminMenu && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<User size={18} />}
                  label="Admin"
                  to={getPath('/users')}
                  isActive={location.pathname === getPath('/users')}
                />
              )}
              {canViewSuperStokezMenu && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<UserCheck size={18} />}
                  label="Super Stokez"
                  to={getPath('/super-stokez')}
                  isActive={location.pathname === getPath('/super-stokez')}
                />
              )}
              {canViewStokezMenu && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<UserCheck size={18} />}
                  label="Stokez"
                  to={getPath('/stokez')}
                  isActive={location.pathname === getPath('/stokez')}
                />
              )}
              {canViewAgentMenu && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<UserPlus size={18} />}
                  label="Agent"
                  to={getPath('/agent')}
                  isActive={location.pathname === getPath('/agent')}
                />
              )}
              {canViewPlayerMenu && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<User size={18} />}
                  label="Player"
                  to={getPath('/player')}
                  isActive={location.pathname === getPath('/player')}
                />
              )}
            </SidebarSection>

            {/* Sales Report Section */}
            {showSalesReportSection && (
            <SidebarSection collapsed={collapsed} icon={<ShoppingCart size={20} />} label="Sales Report">
              {canViewSummaryReport && (
              <MenuItem
                collapsed={collapsed}
                icon={<FileText size={18} />}
                label="Summary Report"
                to={getPath('/reports/summary')}
                isActive={location.pathname === getPath('/reports/summary')}
              />
              )}
              {canViewAllTicket && (
              <MenuItem
                collapsed={collapsed}
                icon={<FileText size={18} />}
                label="All Ticket"
                to={getPath('/reports/all-ticket')}
                isActive={location.pathname === getPath('/reports/all-ticket')}
              />
              )}
              {canViewSuperStockezWiseReport && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<FileText size={18} />}
                  label="Super Stockez Wise"
                  to={getPath('/reports/super-stockez')}
                  isActive={location.pathname === getPath('/reports/super-stockez')}
                />
              )}
              {canViewStokezWiseReport && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<FileText size={18} />}
                  label="Stockez Wise"
                  to={getPath('/reports/stockez')}
                  isActive={location.pathname === getPath('/reports/stockez')}
                />
              )}
              {canViewAgentWiseReport && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<FileText size={18} />}
                  label="Agent Wise"
                  to={getPath('/reports/agent')}
                  isActive={location.pathname === getPath('/reports/agent')}
                />
              )}
              {canViewPlayerWiseReport && (
                <MenuItem
                  collapsed={collapsed}
                  icon={<FileText size={18} />}
                  label="Player Wise"
                  to={getPath('/reports/player')}
                  isActive={location.pathname === getPath('/reports/player')}
                />
              )}
              {canViewPlayerWiseReport && (
              <MenuItem
                collapsed={collapsed}
                icon={<FileText size={18} />}
                label="Show All"
                to={getPath('/reports/allplayer')}
                isActive={location.pathname === getPath('/reports/allplayer')}
              />
              )}
              {canViewSummaryReport && (
              <MenuItem
                collapsed={collapsed}
                icon={<FileText size={18} />}
                label="Show All with Summary"
                to={getPath('/reports/all-summary')}
                isActive={location.pathname === getPath('/reports/all-summary')}
              />
              )}
              {canViewGameWiseReport && (
              <MenuItem
                collapsed={collapsed}
                icon={<FileText size={18} />}
                label="Game Wise"
                to={getPath('/reports/game')}
                isActive={location.pathname === getPath('/reports/game')}
              />
              )}
              {canViewSearchBarcode && (
              <MenuItem
                collapsed={collapsed}
                icon={<FileText size={18} />}
                label="Search Barcode"
                to={getPath('/reports/search')}
                isActive={location.pathname === getPath('/reports/search')}
              />
              )}
            </SidebarSection>
            )}
          </div>

          {/* Standalone Menu Items */}
          <div className={styles.navSection}>
            {canViewTransactionReport && (
            <MenuItem
              collapsed={collapsed}
              icon={<FileText size={20} />}
              label="Transaction Report"
              to={getPath('/translog')}
              isActive={location.pathname === getPath('/translog')}
            />
            )}
            {canViewActivityLog && (
            <MenuItem
              collapsed={collapsed}
              icon={<FileText size={20} />}
              label="Activity Log"
              to={getPath('/activity-log')}
              isActive={location.pathname === getPath('/activity-log')}
            />
            )}
            {canViewSummaryReport && (
            <MenuItem
              collapsed={collapsed}
              icon={<FileText size={20} />}
              label="Reports"
              to={getPath('/reports')}
              isActive={location.pathname === getPath('/reports')}
            />
            )}
            {canViewPrivilegeMenu && (
            <MenuItem
              collapsed={collapsed}
              icon={<Settings size={20} />}
              label="Privilege Management"
              to={getPath('/privileges')}
              isActive={location.pathname === getPath('/privileges')}
            />
            )}
            {canViewPrivilegeCatalog && (
            <MenuItem
              collapsed={collapsed}
              icon={<Settings size={20} />}
              label="Privilege Catalog"
              to={getPath('/privilege-catalog')}
              isActive={location.pathname === getPath('/privilege-catalog')}
            />
            )}
            {canViewSettings && (
            <MenuItem
              collapsed={collapsed}
              icon={<Settings size={20} />}
              label="Settings"
              to={getPath('/settings')}
              isActive={location.pathname === getPath('/settings')}
            />
            )}
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
